import { CommandBarButton, DetailsList, IBasePickerSuggestionsProps, IButtonStyles, IColumn, ICommandBarItemProps, ICommandBarStyles, Icon, IContextualMenuItem, IIconProps, IPanelStyles, IPersonaProps, IRenderFunction, IStackItemStyles, Label, mergeStyles, NormalPeoplePicker, Panel, PanelType, Persona, PersonaSize, PrimaryButton, ScrollablePane, ScrollbarVisibility, SelectionMode, Spinner, SpinnerSize, Stack, StackItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { IGraphGetUsersRequest, IGraphGetUsersResponse } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-interfaces";
import { useLazyGetGroupsQuery, useLazyGetUsersQuery } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-slice";
import { useLazyAddUsersToGroupQuery, useLazyBreakRoleInheritanceOfListQuery, useLazyDeleteLibraryCurrentRoleForGroupQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { CMErrorModal } from "./CMErrorModal";

export const panelActionButtonStyle: Partial<IButtonStyles> = {
    root: {
      marginTop: 20,
    }
  }

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 145px)",
    marginTop: 5,
    marginBottom: 10
});

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 5,
        paddingRight: 5
    },
    navigation: {
        justifyContent: "space-between",
        marginTop: 5
    }
}

export interface PersonaUser extends IPersonaProps {
    role?: string;
    roleId?: number;
    groupTitle?: string;
    userId?: number;
    userGuid?: string;
    groupId?: number;
    loginName?: string;
    email?: string;
    userType?: string;
    roleAssignment?: any;
    roleDefinitionId?: number;
    canEdit?: boolean;
    canRemoveFromGroup?: boolean;
    isGroup?: boolean;
}

export interface ICMPermissionsAddMembersPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    closeOnSuccess: () => void;
    isInOneDrive: boolean;
    groupsUsers: PersonaUser[];
    sharePointListId?: string;
    roleAssignments: any[];
    roleDefinitions: any[];

}

export const CMPermissionsAddMembersPanel = ({ panelIsOpen, closePanel, closeOnSuccess, roleAssignments, roleDefinitions, isInOneDrive, groupsUsers, sharePointListId }: ICMPermissionsAddMembersPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);

    const picker = React.useRef(null);

    const [getUsers] = useLazyGetUsersQuery();
    const [getGroups] = useLazyGetGroupsQuery();
    
    const [addUsersToGroup] = useLazyAddUsersToGroupQuery();
    const [breakRoleInheritanceOfList] = useLazyBreakRoleInheritanceOfListQuery();

    const [selectedUsers, setSelectedUsers] = React.useState<PersonaUser[]>([]);
    const [columns, setColumns] = React.useState<IColumn[]>([]);
    const [isAddingUsers, setIsAddingUsers] = React.useState<boolean>(false);
    const [isCMErrorModalOpen, setIsCMErrorModalOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    const suggestionProps: IBasePickerSuggestionsProps = {
        noResultsFoundText: languageStrings.GenericNoResultsFoundText,
        loadingText: languageStrings.Loading,
      };

    React.useEffect(() => {
        setColumns([
            {
                key: 'name',
                name: languageStrings.Name,
                minWidth: 200,
                maxWidth: 200,
                isResizable: true,
                onRender: (item: any) => {
                    return (
                        <Persona
                            text={item.text}
                            secondaryText={item.secondaryText}
                            size={PersonaSize.size40}
                        />
                    )
                }
            },
            {
                key: 'role',
                name: "",
                minWidth: 30,
                maxWidth: 30,
                isResizable: false,
                onRender: (item: any) => {
                    return (
                        <Stack horizontalAlign="center" verticalAlign="center">
                            <Stack.Item>
                                <CommandBarButton
                                    styles={{ root: { marginTop: 10, paddingTop: 5, paddingBottom: 5 } }}
                                    text={item.role}
                                    disabled={isAddingUsers}
                                    menuProps={{
                                        items: getRoleOptions(item)
                                    }}
                                />
                            </Stack.Item>

                        </Stack>
                    )
                }
            }
        ]);
    }, [selectedUsers, isAddingUsers])

    const getRoleOptions = (item: PersonaUser) => {
        const options: IContextualMenuItem[] = [];

        if (!item.isGroup) {
            roleAssignments.forEach(role => {
                if (role.Member.PrincipalType === 8) {
                    options.push(
                        {
                            key: role.PrincipalId,
                            text: role.Member.Title,
                            //isChecked: true,
                            onClick: () => {
                                setSelectedUsers(selectedUsers.map(user => {
                                    if (user.secondaryText === item.secondaryText) {
                                        return {
                                            ...user,
                                            role: role.Member.Title,
                                            groupId: role.PrincipalId,
                                            roleDefinitionId: null
                                        };
                                    } else {
                                        return user
                                    }
                                }));
                            }
                        }
                    )
                }
            });
        }

        roleDefinitions?.filter(x => x.Hidden === false).forEach(roledef => {
            options.push(
                {
                    key: roledef.Id,
                    text: roledef.Name,
                    //isChecked: true,
                    onClick: () => {
                        setSelectedUsers(selectedUsers.map(user => {
                            if (user.secondaryText === item.secondaryText) {
                                return {
                                    ...user,
                                    role: roledef.Name,
                                    roleDefinitionId: roledef.Id,
                                    groupId: null
                                };
                            } else {
                                return user
                            }
                        }));
                    }
                }
            )


        });

        options.push(
            {
                key: 'remove',
                text: languageStrings.Remove, onClick: () => {
                    setSelectedUsers(selectedUsers.filter(x => x.secondaryText !== item.secondaryText).map(user => {
                        return user;
                    }))
                }
            });

        return options;
    }

    const onGetUsers = async (filterText: string) => {
        const request: IGraphGetUsersRequest = {
            filter: filterText,
            orderby: 'displayName',
            select: 'id, displayName, mail'
        }

        try {
            const results = await getUsers({request, accessToken: await authModule.getGraphAccessToken()}).unwrap();

            return results.value
        } catch (error) {
            logger(error, "CMPermissionsAddMembersPanel.tsx (onGetUsers)");
        }
    }

    const onGetGroups = async (filterText: string) => {
        const request: IGraphGetUsersRequest = {
            filter: filterText,
            orderby: 'displayName',
            select: 'id, displayName, mail'
        }

        try {
            const results = await getGroups({ request, accessToken: await authModule.getGraphAccessToken() }).unwrap();

            return results.value
        } catch (error) {
            logger(error, "CMPermissionsAddMembersPanel.tsx (onGetGroups)");
            return [];
        }
    }

    const onFilterChanged = async (filterText: string) => {
        if (filterText) {
            const [users, groups] = await Promise.all([
                onGetUsers(filterText),
                onGetGroups(filterText),
            ]);
            
            const personas: PersonaUser[] = [];
            const editRoleDef = roleDefinitions.find(x => x.Id === 1073741830);

            (groups as IGraphGetUsersResponse[]).forEach(group => {
                if (!selectedUsers.some(x => x.userGuid === group.id)) {
                    personas.push({
                        text: `${group.displayName} (${languageStrings.Group.toLowerCase()})`,
                        secondaryText: group.mail ? group.mail : languageStrings.NoEmailAvailable,
                        tertiaryText: editRoleDef?.Name,
                        role: editRoleDef?.Name,
                        roleDefinitionId: editRoleDef?.Id,
                        userId: 0,
                        loginName: '',
                        email: group.mail,
                        userGuid: group.id,
                        isGroup: true
                    })
                }
            });

            (users as IGraphGetUsersResponse[]).forEach(user => {
                if (!selectedUsers.some(x => x.email === user.id)) {
                    personas.push({
                        text: user.displayName,
                        secondaryText: user.mail,
                        tertiaryText: editRoleDef?.Name,
                        role: editRoleDef?.Name,
                        roleDefinitionId: editRoleDef?.Id,
                        userId: 0,
                        loginName: '',
                        email: user.mail,
                        userGuid: user.id,
                        isGroup: false
                    })
                }
            });

            return personas;
        }

        return [];
    };

    const onSelectedPersonasChange = (items: PersonaUser[]) => {
        if (items.length > 0) {
            const copyOfMembers = [...selectedUsers];
            copyOfMembers.push(items[0]);
            setSelectedUsers(copyOfMembers);
        }
      }

    const addUser = async (user?: PersonaUser) => {
        const accessToken = isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken();

        try {
            let res = await addUsersToGroup({
                siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                accessToken,
                roleValue: user.groupId ? `group:${user.groupId}` : `role:${user.roleDefinitionId}`,
                users: [user],
                sharePointListId
            }).unwrap();

            if (res.StatusCode === -1) {
                res = await addUsersToGroup({
                    siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                    accessToken,
                    roleValue: user.groupId ? `group:${user.groupId}` : `role:${user.roleDefinitionId}`,
                    users: [user],
                    loginNameTypeOveride: 'c:0o.c|federateddirectoryclaimprovider',
                    sharePointListId
                }).unwrap();
            }

            return res;
        } catch (error) {
            logger(error, "CMPermissionsAddMembersPanel.tsx (addUser)");
        }
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={`${languageStrings.MembersAdd} `}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
        >

            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <div className="xb-content">
                        <Label required>{languageStrings.MemberSelect}</Label>
                        <NormalPeoplePicker
                            onChange={onSelectedPersonasChange}
                            // eslint-disable-next-line react/jsx-no-bind
                            onResolveSuggestions={onFilterChanged}
                            // eslint-disable-next-line react/jsx-no-bind
                            pickerSuggestionsProps={suggestionProps}
                            className={'ms-PeoplePicker'}
                            key={'controlled'}
                            removeButtonAriaLabel={'Remove'}
                            componentRef={picker}
                            selectedItems={[]}
                            resolveDelay={300}
                        />
                        {selectedUsers?.length > 0 && <DetailsList
                            items={selectedUsers}
                            setKey="set"
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="select row"
                        />}
                        {selectedUsers?.length === 0 &&
                            <Stack horizontalAlign="center" styles={{root: {paddingTop: 25}}}>
                                <Label disabled>{languageStrings.MembersNotSelected}</Label>
                            </Stack>
                        }
                    </div>
                </ScrollablePane>
            </div>
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }} style={{ paddingRight: 10 }}>
                <PrimaryButton
                    styles={panelActionButtonStyle}
                    iconProps={{ iconName: selectedUsers?.length > 1 ? 'PeopleAdd' : 'AddFriend' }}
                    disabled={!(selectedUsers?.length > 0) || isAddingUsers}
                    onClick={async () => {
                        setIsAddingUsers(true);

                        if (sharePointListId) {
                            try {
                                await breakRoleInheritanceOfList({
                                    siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                                    accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAdminAccessToken(),
                                    sharePointListId
                                }).unwrap();
                            } catch (error) {
                                logger(error, "CMPermissionsPanel.tsx (onMoveUserOrGroupRole(breakRoleInheritanceOfList))");
                            }
                        }

                        await Promise.all(selectedUsers.map(async user => {
                            await addUser(user)
                        }));

                        setIsAddingUsers(false);
                        closeOnSuccess();
                    }}
                    text={languageStrings.Add}
                >
                    {isAddingUsers && <Spinner size={SpinnerSize.small} style={{paddingLeft: '5px'}}/>}
                </PrimaryButton>
            </Stack>
             {isCMErrorModalOpen && <CMErrorModal message={errorMessage} showDialog={isCMErrorModalOpen} closeDialog={() => setIsCMErrorModalOpen(false)}/>}

        </Panel>
    )
};

